import * as actions from './actions';
import * as AIAssistantApi from '../../../api/AIAssistantApi';
import { v4 as uuidv4 } from 'uuid';
import StreamHandler from '../../../utils/aiAssistant/streamHandler';
import { executeActionHandler } from '../../../utils/aiAssistant/actionHandlers';

const ACTION_BLOCK_REGEX = /<!--ACTION:(.*?)-->/s;

const parseActionBlock = (content) => {
  const match = content.match(ACTION_BLOCK_REGEX);
  if (!match) return null;
  try {
    const parsed = JSON.parse(match[1]);

    if (parsed.type === 'CREATE_COURSE') {
      return {
        type: parsed.type,
        data: {
          title: parsed.title,
          description: parsed.description,
          sections: parsed.sections,
        },
        preview: {
          before: null,
          after: parsed.title + (parsed.sections ? ` with ${parsed.sections.length} sections` : ''),
        },
        label: parsed.label || 'Course',
      };
    }

    if (parsed.type === 'CREATE_MEMBERSHIP') {
      return {
        type: parsed.type,
        data: {
          title: parsed.title,
          description: parsed.description,
          price: parsed.price,
        },
        preview: {
          before: null,
          after: parsed.title || 'New Membership',
        },
        label: parsed.label || 'Membership',
      };
    }

    if (parsed.type === 'NAVIGATE_TO') {
      return {
        type: parsed.type,
        data: {
          routeName: parsed.routeName,
          route: parsed.route,
          params: parsed.params,
        },
        preview: {
          before: null,
          after: `Navigate to ${parsed.label || parsed.routeName}`,
        },
        label: parsed.label || parsed.routeName,
      };
    }

    return {
      type: parsed.type,
      data: {
        field: parsed.field,
        value: parsed.value,
        ...parsed,
      },
      preview: {
        before: null,
        after: parsed.value || parsed.title,
      },
      label: parsed.label || parsed.field,
    };
  } catch (e) {
    return null;
  }
};

const stripActionBlock = (content) => {
  return content.replace(ACTION_BLOCK_REGEX, '').trim();
};

export const sendMessageOperation = (messageText) => async (dispatch, getState) => {
  try {
    dispatch(actions.sendMessageRequest());

    const state = getState();
    const { context, conversationId } = state.aiAssistant;

    const userMessage = {
      id: uuidv4(),
      role: 'user',
      content: messageText,
      timestamp: new Date().toISOString()
    };

    dispatch(actions.addMessage(userMessage));

    const assistantMessageId = uuidv4();
    const assistantMessage = {
      id: assistantMessageId,
      role: 'assistant',
      content: '',
      timestamp: new Date().toISOString(),
      isStreaming: true
    };

    dispatch(actions.addMessage(assistantMessage));
    dispatch(actions.setTyping(true));

    const streamHandler = new StreamHandler(
      (chunk) => {
        const displayContent = stripActionBlock(chunk);
        dispatch(actions.updateMessage(assistantMessageId, {
          content: displayContent,
          isStreaming: true
        }));
      },
      () => {
        const fullContent = streamHandler.fullContent;
        const displayContent = stripActionBlock(fullContent);
        const actionBlock = parseActionBlock(fullContent);

        console.log('[AI Assistant] Stream complete:', {
          fullContent: fullContent.substring(0, 200),
          actionBlock
        });

        dispatch(actions.updateMessage(assistantMessageId, {
          content: displayContent,
          isStreaming: false
        }));
        dispatch(actions.setTyping(false));
        dispatch(actions.sendMessageSuccess());

        if (actionBlock) {
          console.log('[AI Assistant] Setting pending action:', actionBlock);
          dispatch(actions.setPendingAction(actionBlock));
        } else {
          console.log('[AI Assistant] No action block found in response');
        }
      },
      (error) => {
        dispatch(actions.updateMessage(assistantMessageId, {
          isStreaming: false,
          error: true
        }));
        dispatch(actions.setTyping(false));
        dispatch(actions.sendMessageFailure(error.message));
      },
      (meta) => {
        if (meta.conversationId) {
          dispatch(actions.setConversationId(meta.conversationId));
          try { sessionStorage.setItem('ai_conversation_id', meta.conversationId); } catch (e) { /* noop */ }
        }
      }
    );

    await AIAssistantApi.sendAIMessage({
      message: messageText,
      context,
      conversationId
    }, streamHandler);

  } catch (error) {
    dispatch(actions.setTyping(false));
    dispatch(actions.sendMessageFailure(error.message));
  }
};

export const executeActionOperation = (action) => async (dispatch, getState) => {
  try {
    dispatch(actions.executeActionRequest());

    const result = await executeActionHandler(action, dispatch, getState);

    if (result.success) {
      dispatch(actions.executeActionSuccess());
      dispatch(actions.clearPendingAction());

      dispatch(actions.addMessage({
        id: uuidv4(),
        role: 'system',
        content: result.message || 'Action completed successfully',
        timestamp: new Date().toISOString()
      }));

      await AIAssistantApi.executeAIAction({
        actionType: action.type,
        actionData: action.data,
        status: 'completed'
      });
    } else {
      throw new Error(result.error || 'Action execution failed');
    }
  } catch (error) {
    dispatch(actions.executeActionFailure(error.message));
    dispatch(actions.addMessage({
      id: uuidv4(),
      role: 'system',
      content: `Error: ${error.message}`,
      timestamp: new Date().toISOString(),
      error: true
    }));
  }
};

export const loadConversationOperation = (conversationId) => async (dispatch) => {
  try {
    dispatch(actions.loadConversationRequest());

    const conversation = await AIAssistantApi.getConversation(conversationId);

    dispatch(actions.loadConversationSuccess(conversation));
  } catch (error) {
    dispatch(actions.loadConversationFailure(error.message));
  }
};

export const updateContextOperation = (contextData) => (dispatch) => {
  dispatch(actions.updateContext(contextData));
};

export const toggleSidebarOperation = () => (dispatch) => {
  dispatch(actions.toggleSidebar());
};

export const setSidebarVisibleOperation = (isVisible) => (dispatch) => {
  dispatch(actions.setSidebarVisible(isVisible));
};

export const setSidebarCollapsedOperation = (isCollapsed) => (dispatch) => {
  dispatch(actions.setSidebarCollapsed(isCollapsed));
};

export const cancelActionOperation = () => (dispatch) => {
  dispatch(actions.clearPendingAction());
};

export const clearMessagesOperation = () => (dispatch) => {
  dispatch(actions.clearMessages());
  dispatch(actions.setConversationId(null));
  try { sessionStorage.removeItem('ai_conversation_id'); } catch (e) { /* noop */ }
};

export const restoreConversationOperation = () => async (dispatch) => {
  try {
    const savedId = sessionStorage.getItem('ai_conversation_id');
    if (!savedId) return;

    dispatch(actions.loadConversationRequest());
    const conversation = await AIAssistantApi.getConversation(savedId);
    dispatch(actions.loadConversationSuccess(conversation));
  } catch (error) {
    // Conversation no longer exists or failed to load — clear stale ID
    try { sessionStorage.removeItem('ai_conversation_id'); } catch (e) { /* noop */ }
    dispatch(actions.loadConversationFailure(error.message));
  }
};
